<script lang="ts">
	import { browser } from '$app/env';
	import { FRANCE_CENTER } from '#lib/constants.js';
	import { Map, View } from 'ol';
	import { defaults as defaultControls } from 'ol/control/defaults.js';
	import type { Coordinate } from 'ol/coordinate';
	import type { Extent } from 'ol/extent';
	import { DblClickDragZoom, defaults as defaultInteractions } from 'ol/interaction.js';
	import { setupLambert93Projection } from './projection.js';
	import { onDestroy, onMount, setContext } from 'svelte';

	interface Props {
		center?: Coordinate;
		zoom?: number;
		rotation?: number;
		fit?: Extent;
		map?: Map;
		onViewChange?: (params: {
			zoom: number;
			extent: Extent;
			center: Coordinate;
			rotation: number;
		}) => void;
		children?: import('svelte').Snippet;
	}

	let {
		center = FRANCE_CENTER,
		rotation = 0,
		zoom = 6,
		fit,
		map = $bindable(undefined),
		onViewChange,
		children
	}: Props = $props();

	let view: View | undefined = $state();
	let container: HTMLDivElement;

	$effect(() => {
		if (browser && view !== undefined && fit !== undefined)
			view.fit(fit, { padding: [20, 20, 20, 20] });
	});

	$effect(() => {
		if (browser && view !== undefined && zoom !== undefined) view.setZoom(zoom);
	});

	$effect(() => {
		if (browser && view !== undefined && center !== undefined) view.setCenter(center);
	});

	$effect(() => {
		if (browser && view !== undefined && rotation !== undefined) view.setRotation(rotation);
	});

	setContext('map', () => map);

	onMount(() => {
		setupLambert93Projection();

		view = new View({
			projection: 'EPSG:2154',
			center,
			zoom,
			rotation
		});

		map = new Map({
			target: container,
			interactions: defaultInteractions({ doubleClickZoom: true }).extend([
				new DblClickDragZoom({ delta: -0.01 })
			]),
			view,
			controls: defaultControls({
				attribution: false,
				rotate: true,
				rotateOptions: { autoHide: false }
			})
		});

		map.on('moveend', (event) => {
			const view = event.map.getView();
			const newCenter = event.map.getView().getCenter();
			const rotation = event.map.getView().getRotation();
			const newZoom = event.map.getView().getZoom();
			const extent = view.calculateExtent(event.map.getSize());

			if (onViewChange !== undefined && newZoom !== undefined && newCenter !== undefined) {
				onViewChange({ zoom: newZoom, extent, center: newCenter, rotation });
			}
		});
	});

	onDestroy(() => {
		if (map !== undefined) map.dispose();
	});
</script>

<div bind:this={container} class="map"></div>

{#if map}
	{@render children?.()}
{/if}

<style>
	.map {
		width: 100%;
		height: 100%;
	}

	.map :global(.ol-control) {
		position: absolute;
	}

	.map :global(.ol-control button),
	.map :global(.ol-control button:hover),
	.map :global(.ol-control button:focus) {
		--pico-background-color: white;
		--pico-color: var(--pico-primary-hover);
		--pico-border-color: var(--pico-primary-hover);
	}

	.map :global(.ol-control button) {
		margin: 0;
		font-size: 1.5rem;
		padding: 0.25rem;
		line-height: 1.5rem;
		display: flex;
		width: 2rem;
		height: 2rem;
		justify-content: center;
		align-items: center;
	}

	.map :global(.ol-zoom) {
		top: 0.5rem;
		left: 0.5rem;
		display: flex;
		flex-direction: column;
	}

	.map :global(.ol-zoom .ol-zoom-in) {
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		border-bottom-width: 0;
	}

	.map :global(.ol-zoom .ol-zoom-out) {
		border-top-left-radius: 0;
		border-top-right-radius: 0;
	}

	.map :global(.ol-rotate) {
		top: 5.75rem;
		left: 0.5rem;
	}

	.map :global(.ol-compass) {
		content: '';
		width: 1.5rem;
		height: 1.5rem;
		mask: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxZW0iIGhlaWdodD0iMWVtIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHBhdGggZmlsbD0iY3VycmVudENvbG9yIiBkPSJNNTAuMDMgNWEyLjUxNiAyLjUxNiAwIDAgMC0yLjQzIDEuNzZMMzQuNDkzIDQ4LjU0OGEyLjUxIDIuNTEgMCAwIDAtLjM3MiAxLjQ1NGMtLjAyNi41MS4xMDQgMS4wMTcuMzcyIDEuNDUybDEzLjEwNSA0MS43ODJjLjczNyAyLjM1MiA0LjA2NSAyLjM1MiA0LjgwMiAwbDEzLjEwNS00MS43ODVjLjI3LS40MzYuMzk5LS45NDUuMzcyLTEuNDU2YTIuNTEzIDIuNTEzIDAgMCAwLS4zNzItMS40NUw1Mi40MDEgNi43NkEyLjUxMyAyLjUxMyAwIDAgMCA1MC4wMyA1TTM5LjQwMyA1MC4yODhoNi4yMDVjLjE1MiAyLjMwNiAyLjA0OCA0LjEzNCA0LjM5MiA0LjEzNGMyLjM0NCAwIDQuMjQtMS44MjggNC4zOTItNC4xMzRoNi40NjFMNTAgODQuMDc4WiIgY29sb3I9ImN1cnJlbnRDb2xvciIvPjwvc3ZnPg==)
			no-repeat;
		mask-position: center;
		mask-size: 100% 100%;
		background-color: currentColor;
		will-change: transform;
	}
</style>
